import { memo, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/StoreOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import WorkIcon from "@mui/icons-material/WorkOutline";
import StraightenIcon from "@mui/icons-material/Straighten";
import FilterIcon from "@mui/icons-material/FilterAltOutlined";

import {
  listGradeSizes,
  listGradeAgrupadores,
} from "../../shared/utils/family-grade";

function formatVitrineFilterLabel(vitrineKey, vitrinesMeta) {
  const meta = vitrinesMeta[vitrineKey] || {};
  const nome = (meta.nome || "").trim();
  const codigo = meta.idVitrineVirtualB2b;
  if (nome && codigo != null) return `${nome} · ${codigo}`;
  if (nome) return nome;
  return String(vitrineKey);
}

function FilterSection({
  title,
  icon: Icon,
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  onInputFocus,
  onInputBlur,
  children,
  emptyMessage,
}) {
  return (
    <Box className="filter-section">
      <Typography className="filter-section-title">
        <Icon className="filter-icon" />
        {title}
      </Typography>
      {searchPlaceholder && (
        <Box className="search-input-wrapper-small">
          <FilterIcon className="search-icon" />
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input-small"
            variant="outlined"
            InputProps={{ className: "search-input-inner" }}
            inputProps={{ style: { paddingLeft: "28px", fontSize: "12px" } }}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </Box>
      )}
      <Box className="filter-options scrollable">
        {children}
        {emptyMessage && (
          <Typography className="no-results">{emptyMessage}</Typography>
        )}
      </Box>
    </Box>
  );
}

function Sidebar({
  currentBrand,
  families,
  selectedCommercialNames,
  selectedVitrines,
  selectedAgrupadores,
  selectedSizes,
  productSearchTerm,
  setProductSearchTerm,
  toggleFilter,
  theme,
  vitrinesMeta = {},
}) {
  const [commercialSearchTerm, setCommercialSearchTerm] = useState("");
  const [agrupadorSearchTerm, setAgrupadorSearchTerm] = useState("");
  const [sizeSearchTerm, setSizeSearchTerm] = useState("");
  const [vitrineSearchTerm, setVitrineSearchTerm] = useState("");

  // brandFamilies é referencialmente estável (vem do useMemo de useVitrineData),
  // mas Object.keys() gerava um array novo a cada render, invalidando os useMemo
  // abaixo em todo render. Deriva-se `keys` dentro dos useMemo, dependendo só de
  // brandFamilies, para que essas varreduras O(famílias) só rodem quando a marca muda.
  const brandFamilies = families[currentBrand] || {};

  const agrupadores = useMemo(() => {
    const subs = new Set();
    Object.keys(brandFamilies).forEach((key) => {
      listGradeAgrupadores(brandFamilies[key]).forEach((sub) => subs.add(sub));
    });
    return Array.from(subs).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies]);

  const commercialNames = useMemo(() => {
    const comms = new Set();
    Object.keys(brandFamilies).forEach((key) => {
      const fam = brandFamilies[key];
      const name = String(
        fam.gradeVenda || fam.commercialName || fam.productDisplayName || "",
      ).trim();
      if (name) comms.add(name);
    });
    return Array.from(comms).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies]);

  const sizes = useMemo(() => {
    const set = new Set();
    Object.keys(brandFamilies).forEach((key) => {
      listGradeSizes(brandFamilies[key]).forEach((size) => set.add(size));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [brandFamilies]);

  const vitrines = useMemo(() => {
    const vits = new Set();
    Object.keys(brandFamilies).forEach((key) => {
      const fam = brandFamilies[key];
      if (fam.vitrine != null) vits.add(fam.vitrine);
      (fam.products || []).forEach((product) => {
        if (product.vitrine != null) vits.add(product.vitrine);
      });
    });
    return Array.from(vits).sort((a, b) => {
      const la = formatVitrineFilterLabel(a, vitrinesMeta);
      const lb = formatVitrineFilterLabel(b, vitrinesMeta);
      return la.localeCompare(lb, "pt-BR");
    });
  }, [brandFamilies, vitrinesMeta]);

  const filterByTerm = (list, term) => {
    const t = term.trim().toLowerCase();
    if (!t) return list;
    return list.filter((item) => String(item).toLowerCase().includes(t));
  };

  const filteredAgrupadores = useMemo(
    () => filterByTerm(agrupadores, agrupadorSearchTerm),
    [agrupadores, agrupadorSearchTerm],
  );

  const filteredCommercialNames = useMemo(
    () => filterByTerm(commercialNames, commercialSearchTerm),
    [commercialNames, commercialSearchTerm],
  );

  const filteredSizes = useMemo(
    () => filterByTerm(sizes, sizeSearchTerm),
    [sizes, sizeSearchTerm],
  );

  const filteredVitrines = useMemo(() => {
    const t = vitrineSearchTerm.trim().toLowerCase();
    if (!t) return vitrines;
    return vitrines.filter((vKey) => {
      const meta = vitrinesMeta[vKey] || {};
      const nome = String(meta.nome || "").toLowerCase();
      const codigo = String(meta.idVitrineVirtualB2b ?? vKey).toLowerCase();
      const label = formatVitrineFilterLabel(vKey, vitrinesMeta).toLowerCase();
      return nome.includes(t) || codigo.includes(t) || label.includes(t);
    });
  }, [vitrines, vitrineSearchTerm, vitrinesMeta]);

  const handleInputFocus = (e) => {
    const inputRoot = e.target.closest(".MuiOutlinedInput-root");
    if (inputRoot) {
      const fieldset = inputRoot.querySelector("fieldset");
      if (fieldset) fieldset.style.borderColor = theme.primary;
    }
  };

  const handleInputBlur = (e) => {
    const inputRoot = e.target.closest(".MuiOutlinedInput-root");
    if (inputRoot) {
      const fieldset = inputRoot.querySelector("fieldset");
      if (fieldset) fieldset.style.borderColor = "#e5e7eb";
    }
  };

  const checkboxOption = (type, value, checked, label) => (
    <FormControlLabel
      key={`${type}-${value}`}
      control={
        <Checkbox
          checked={checked}
          onChange={() => toggleFilter(type, value)}
          className="b2b-checkbox"
          color="default"
          style={{ color: checked ? theme.primary : "#9ca3af" }}
          icon={
            <span
              className="sidebar-checkbox-icon sidebar-checkbox-icon--off"
              style={{ borderColor: "#d1d5db" }}
            />
          }
          checkedIcon={
            <span
              className="sidebar-checkbox-icon sidebar-checkbox-icon--on"
              style={{
                borderColor: theme.primary,
                backgroundColor: theme.primary,
              }}
            >
              ✓
            </span>
          }
        />
      }
      label={label}
      className="filter-option"
    />
  );

  return (
    <Box className="sidebar-container">
      <Box className="sidebar-search">
        <Typography className="sidebar-section-title">
          Filtrar Pedido
        </Typography>
        <Box className="search-input-wrapper">
          <SearchIcon className="search-icon" />
          <TextField
            fullWidth
            placeholder="Buscar Ref. ou Produto"
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
            className="search-input"
            variant="outlined"
            InputProps={{ className: "search-input-inner", startAdornment: null }}
            inputProps={{ style: { paddingLeft: "36px" } }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </Box>
      </Box>

      <Box className="sidebar-filters">
        <FilterSection
          title="Vitrine"
          icon={StoreIcon}
          searchPlaceholder="Buscar nome ou código..."
          searchTerm={vitrineSearchTerm}
          onSearchChange={setVitrineSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredVitrines.length === 0 ? "Nenhuma vitrine encontrada." : null
          }
        >
          {filteredVitrines.map((v) =>
            checkboxOption(
              "vitrine",
              v,
              selectedVitrines.has(v),
              formatVitrineFilterLabel(v, vitrinesMeta),
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Nome comercial/Grade"
          icon={WorkIcon}
          searchPlaceholder="Buscar nome comercial/grade..."
          searchTerm={commercialSearchTerm}
          onSearchChange={setCommercialSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredCommercialNames.length === 0
              ? "Nenhum nome comercial encontrado."
              : null
          }
        >
          {filteredCommercialNames.map((cn) =>
            checkboxOption(
              "commercial",
              cn,
              selectedCommercialNames.has(cn),
              cn,
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Item"
          icon={LayersIcon}
          searchPlaceholder="Buscar item..."
          searchTerm={agrupadorSearchTerm}
          onSearchChange={setAgrupadorSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredAgrupadores.length === 0 ? "Nenhum item encontrado." : null
          }
        >
          {filteredAgrupadores.map((sub) =>
            checkboxOption(
              "agrupador",
              sub,
              selectedAgrupadores.has(sub),
              sub,
            ),
          )}
        </FilterSection>

        <FilterSection
          title="Tamanho"
          icon={StraightenIcon}
          searchPlaceholder="Buscar tamanho..."
          searchTerm={sizeSearchTerm}
          onSearchChange={setSizeSearchTerm}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
          emptyMessage={
            filteredSizes.length === 0 ? "Nenhum tamanho encontrado." : null
          }
        >
          {filteredSizes.map((size) =>
            checkboxOption("size", size, selectedSizes.has(size), size),
          )}
        </FilterSection>
      </Box>
    </Box>
  );
}

// Memoizada: a Sidebar não depende do orderState/tempGridState, então não deve
// re-renderizar quando o usuário digita quantidades (o que re-renderiza a página).
export default memo(Sidebar);
